class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this.openAfterLocalizedRefresh();
  }

  openAfterLocalizedRefresh() {
    const url = new URL(window.location.href);
    if (url.searchParams.get('_sn_cart_open') !== '1') return;

    url.searchParams.delete('_sn_cart_open');
    window.history.replaceState({}, '', url.toString());
    setTimeout(() => this.open(), 0);
  }

  reloadAndOpen() {
    const url = new URL(window.location.href);
    url.searchParams.set('_sn_cart_open', '1');
    window.location.assign(url.toString());
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink)
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {this.classList.add('animate', 'active')});

    this.addEventListener('transitionend', () => {
      const containerToTrapFocusOn = this.classList.contains('is-empty') ? this.querySelector('.drawer__inner-empty') : document.getElementById('CartDrawer');
      const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
      trapFocus(containerToTrapFocusOn, focusElement);
    }, { once: true });

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if(cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  async renderContents(parsedState) {
    this.productId = parsedState && parsedState.id;
    let sections = parsedState && parsedState.sections;

    if (!this.hasRenderedSections(sections)) {
      // A successful add can omit sections or return null section values.
      // Refresh only the rendered cart; never repeat the add request.
      sections = await this.fetchRenderedSections();
    }

    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
      const sectionHtml = sections[section.id];
      const innerHtml = this.getSectionInnerHTML(sectionHtml, section.selector);

      if (sectionElement && innerHtml !== null) {
        sectionElement.innerHTML = innerHtml;
      }
    });

    this.classList.toggle('is-empty', Boolean(this.querySelector('.drawer__inner-empty')));

    setTimeout(() => {
      const overlay = this.querySelector('#CartDrawer-Overlay');
      if (overlay) overlay.addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  hasRenderedSections(sections) {
    return Boolean(sections) && this.getSectionsToRender().every((section) => {
      const source = this.getSectionDOM(sections[section.id], section.selector);
      return Boolean(source && (section.id !== 'cart-drawer' || source.querySelector('.drawer__inner')));
    });
  }

  async fetchRenderedSections() {
    const sectionIds = this.getSectionsToRender().map((section) => section.id);
    const url = new URL(window.location.href);
    url.hash = '';
    url.searchParams.delete('section_id');
    url.searchParams.set('sections', sectionIds.join(','));
    url.searchParams.set('_sn_cart_refresh', String(Date.now()));
    const options = {
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'application/json', 'Cache-Control': 'no-cache' }
    };

    try {
      const response = await fetch(url.toString(), options);
      if (response.ok) {
        const sections = await response.json();
        if (this.hasRenderedSections(sections)) return sections;
      }
    } catch (error) {
      // Translation layers can return page HTML instead of section JSON.
      // The localized cart page below is a read-only fallback for that case.
    }

    const cartUrl = new URL(window.routes.cart_url, window.location.origin);
    ['preview_theme_id', 'preview_key', '_fd', '_sc'].forEach((key) => {
      if (url.searchParams.has(key)) cartUrl.searchParams.set(key, url.searchParams.get(key));
    });
    cartUrl.searchParams.set('_sn_cart_refresh', String(Date.now()));
    const response = await fetch(cartUrl.toString(), {
      ...options,
      headers: { ...options.headers, Accept: 'text/html' }
    });
    if (!response.ok) throw new Error('Unable to refresh the cart drawer.');
    const parsedDocument = new DOMParser().parseFromString(await response.text(), 'text/html');
    const drawer = parsedDocument.querySelector('cart-drawer');
    const icon = parsedDocument.querySelector('#cart-icon-bubble');
    const sections = {
      'cart-drawer': drawer && drawer.outerHTML,
      'cart-icon-bubble': icon && `<div class="shopify-section">${icon.innerHTML}</div>`
    };
    if (!this.hasRenderedSections(sections)) throw new Error('The refreshed cart is missing its sections.');
    return sections;
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    if (!html) return null;

    const source = new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector);

    return source ? source.innerHTML : null;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer'
      },
      {
        id: 'cart-icon-bubble'
      }
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    if (typeof html !== 'string') return null;
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      }
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
